import { ChartData } from 'chart.js';
import { GraphOption } from 'components/MuscleDistributionGraphSelector';
import { HevyMuscleSplitRadarGraphOptions } from 'components/MuscleSplitRadarGraphOptions';
import {
  MuscleGroup,
  SetCountableWorkoutOrRoutine,
  calculateSimplifiedMuscleSplit,
  getMuscleHeatmapDataForWorkoutsOrRoutines,
  calculateSetCountPerMuscleGroups,
} from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import { ViewRoutineExercise } from 'screens/Routines/types';
import { colors } from 'styleguide/colors';
import { ExerciseTemplateAggregator } from 'state/aggregators/exerciseTemplateAggregatorStore';
import { Client } from 'types/client';

export class RoutineSummaryViewModel {
  exercises: ViewRoutineExercise[] = [];
  client?: Client;
  selectedMuscleGroupGraph: GraphOption = 'body';

  constructor(exercises: ViewRoutineExercise[], client?: Client) {
    makeAutoObservable(this);
    this.exercises = exercises;
    this.client = client;
  }

  get totalSets() {
    return this.exercises.reduce((acc, curr) => acc + curr.sets.length, 0);
  }

  get radarGraphOptions() {
    const options = HevyMuscleSplitRadarGraphOptions;
    options.animation = false;
    return options;
  }

  get heatMapData() {
    return getMuscleHeatmapDataForWorkoutsOrRoutines({
      workoutsOrRoutines: [this.routineWithMuscleGroups],
    });
  }

  get muscleGroupData() {
    const setsPerMuscleGroup: { muscleGroupName: MuscleGroup; numberOfSets: number }[] = [];
    const rawData = calculateSetCountPerMuscleGroups({
      workoutsOrRoutines: [this.routineWithMuscleGroups],
      includeWarmupSets: false,
    });
    Object.keys(rawData).forEach(muscleGroup => {
      setsPerMuscleGroup.push({
        muscleGroupName: muscleGroup as MuscleGroup,
        numberOfSets: rawData[muscleGroup as MuscleGroup] ?? 0,
      });
    });
    return setsPerMuscleGroup;
  }

  get routineWithMuscleGroups(): SetCountableWorkoutOrRoutine {
    return {
      exercises: this.exercises
        .filter(e => {
          return !!ExerciseTemplateAggregator.getExerciseTemplate(
            e.templateId,
            e.parentTemplateId,
            this.client,
          );
        })
        .map(e => {
          const template = ExerciseTemplateAggregator.getExerciseTemplate(
            e.templateId,
            e.parentTemplateId,
            this.client,
          );

          if (!template) {
            throw new Error('Unable to find template!');
          }
          return {
            ...e,
            sets: e.sets.map(s => ({
              indicator: typeof s.indicator === 'number' ? 'normal' : s.indicator,
            })),
            muscle_group: template.muscle_group,
            other_muscles: template.other_muscles,
          };
        }),
    };
  }

  get radarGraphData(): ChartData<'radar'> {
    const muscleSplitData = calculateSimplifiedMuscleSplit({
      workoutsOrRoutines: [this.routineWithMuscleGroups],
      includeWarmupSets: false,
    });
    const labels = ['Core', 'Shoulders', 'Arms', 'Legs', 'Back', 'Chest'];
    const data = [
      muscleSplitData.core,
      muscleSplitData.shoulders,
      muscleSplitData.arms,
      muscleSplitData.legs,
      muscleSplitData.back,
      muscleSplitData.chest,
    ];
    return {
      labels: labels,
      datasets: [
        {
          data: data,
          borderColor: colors.primary500,
          borderWidth: 1,
        },
      ],
    };
  }

  onSelectedMuscleGroupGraphChanged = (selected: GraphOption) => {
    this.selectedMuscleGroupGraph = selected;
  };
}
